// GestionMovimientosCaja.tsx — Co Marca — Movimientos de Caja
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Plus, ArrowDownCircle, ArrowUpCircle, TrendingUp, TrendingDown, Wallet, DollarSign, X, FileText } from 'lucide-react';
import { registrarMovimiento, obtenerMovimientosPorSesion, MovimientoCaja } from '../../../services/pos/movimientoCajaService';
import { useWebSocketMovimientosCaja } from '../../../hooks/useWebSocketMovimientosCaja';
interface GestionMovimientosCajaProps {
    sesionId: number;
    saldoInicial: number;
    /**
     * Grant del operador identificado por PIN sobre ESTA caja. El backend lo
     * exige para todo asiento manual y atribuye el movimiento al operador;
     * sin grant el registro se bloquea en la UI (nunca falla en el servidor).
     */
    operadorToken: string | null;
}

const C = {
    brand:        '#61257b',   // Púrpura primario (Co Marca)
    brandDark:    '#3d1250',   // Púrpura oscuro
    brandLight:   '#8b3fa8',   // Púrpura suave
    accent1:      '#c1348b',   // Magenta / Acento principal
    accent1Dark:  '#8f1f64',   // Magenta oscuro
    accent2:      '#ea8406',   // Naranja cálido
    accent2Dark:  '#b86300',   // Naranja oscuro
    accent3:      '#ffd97c',   // Amarillo dorado
    accent3Dark:  '#f0b429',   // Dorado intenso
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
.gmc * { font-family:'Plus Jakarta Sans',sans-serif !important; box-sizing:border-box; }
@keyframes gmcSpin { to{transform:rotate(360deg);} }
@keyframes gmcFadeUp { from{opacity:0;transform:translateY(14px);}to{opacity:1;transform:translateY(0);} }
@keyframes gmcFadeIn { from{opacity:0;}to{opacity:1;} }
.gmc-fadeup { animation:gmcFadeUp .35s cubic-bezier(.16,1,.3,1) both; }
.gmc-row { transition:background .15s; }
.gmc-row:hover { background:#fcfafd; }
.gmc-input {
    width:100%;padding:10px 14px;border-radius:10px;
    border:1.5px solid #e5e7eb;background:#fafafa;
    font-size:.88rem;color:#1e293b;outline:none;transition:all .2s;
    font-family:'Plus Jakarta Sans',sans-serif;
}
.gmc-input:focus { border-color:${C.brand};box-shadow:0 0 0 3px rgba(97,37,123,.12);background:#fff; }
.gmc-scroll::-webkit-scrollbar{width:5px;height:5px;}
.gmc-scroll::-webkit-scrollbar-track{background:transparent;}
.gmc-scroll::-webkit-scrollbar-thumb{background:rgba(97,37,123,.2);border-radius:10px;}
.gmc-scroll::-webkit-scrollbar-thumb:hover{background:rgba(97,37,123,.35);}
.gmc-type-btn { flex:1;padding:10px 8px;border-radius:10px;border:2px solid;display:flex;align-items:center;justify-content:center;gap:6px;font-size:.85rem;font-weight:700;cursor:pointer;transition:all .2s;font-family:'Plus Jakarta Sans',sans-serif; }
.gmc-btn-primary { display:inline-flex;align-items:center;gap:6px;padding:9px 18px;border-radius:11px;border:none;font-size:.82rem;font-weight:800;cursor:pointer;transition:all .22s cubic-bezier(.16,1,.3,1);font-family:'Plus Jakarta Sans',sans-serif; }
.gmc-btn-ghost   { display:inline-flex;align-items:center;gap:6px;padding:9px 14px;border-radius:11px;border:1.5px solid #e2e8f0;background:#f8fafc;color:#374151;font-size:.82rem;font-weight:700;cursor:pointer;transition:all .2s;font-family:'Plus Jakarta Sans',sans-serif; }
.gmc-btn-ghost:hover  { background:#f1f5f9; }
`;

const GestionMovimientosCaja: React.FC<GestionMovimientosCajaProps> = ({ sesionId, saldoInicial, operadorToken }) => {
    const [movimientos, setMovimientos]       = useState<MovimientoCaja[]>([]);
    const [showModal, setShowModal]           = useState(false);
    const [tipo, setTipo]                     = useState<'entrada'|'salida'>('entrada');
    const [monto, setMonto]                   = useState('');
    const [descripcion, setDescripcion]       = useState('');
    const [loading, setLoading]               = useState(false);
    const [cargando, setCargando]             = useState(true);
    const modalRef = useRef<HTMLDivElement | null>(null);
    const movimientosRequestRef = useRef(0);
    const registrarRequestRef = useRef(0);
    const currentSessionRef = useRef(sesionId);
    currentSessionRef.current = sesionId;

    useEffect(() => {
        ++registrarRequestRef.current;
        setLoading(false);
        setShowModal(false);
        setMonto('');
        setDescripcion('');
    }, [sesionId]);

    // Sin operador identificado no puede haber un registro en curso ni abierto.
    useEffect(() => {
        if (operadorToken) return;
        ++registrarRequestRef.current;
        setLoading(false);
        setShowModal(false);
    }, [operadorToken]);

    const cargarMovimientos = useCallback(async () => {
        const requestId = ++movimientosRequestRef.current;
        setMovimientos([]);
        try {
            setCargando(true);
            const result = await obtenerMovimientosPorSesion(sesionId);
            if (requestId === movimientosRequestRef.current) setMovimientos(result);
        } catch(e) {
            if (requestId === movimientosRequestRef.current) setMovimientos([]);
            console.error(e);
        }
        finally { if (requestId === movimientosRequestRef.current) setCargando(false); }
    }, [sesionId]);

    useWebSocketMovimientosCaja((data) => {
        console.log('[GestionMovimientosCaja] Evento WebSocket recibido, recargando...', data);
        cargarMovimientos();
    }, sesionId);

    useEffect(() => { cargarMovimientos(); }, [cargarMovimientos]);

    useEffect(() => {
        if (!showModal) return;
        const previousFocus = document.activeElement as HTMLElement | null;
        const dialog = modalRef.current;
        const selector = 'button:not([disabled]),input:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
        window.setTimeout(() => (dialog?.querySelector<HTMLElement>(selector) || dialog)?.focus(), 0);
        const onKeyDown = (event: KeyboardEvent) => {
            if (!dialog) return;
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopImmediatePropagation();
                setShowModal(false); setMonto(''); setDescripcion('');
                return;
            }
            if (event.key !== 'Tab') return;
            const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(selector));
            if (!focusable.length) { event.preventDefault(); dialog.focus(); return; }
            const first = focusable[0], last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        };
        document.addEventListener('keydown', onKeyDown, true);
        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
            if (previousFocus?.isConnected) previousFocus.focus();
        };
    }, [showModal]);

    const handleRegistrar = async () => {
        if (!monto || parseFloat(monto) <= 0) { alert('Ingrese un monto válido'); return; }
        if (!operadorToken) { alert('Identifica al operador de caja con su PIN antes de registrar movimientos.'); return; }
        const requestId = ++registrarRequestRef.current;
        const requestSessionId = sesionId;
        setLoading(true);
        try {
            await registrarMovimiento({
                sesion: requestSessionId,
                tipo,
                monto: parseFloat(monto),
                descripcion,
            }, operadorToken);
            if (requestId !== registrarRequestRef.current || requestSessionId !== currentSessionRef.current) return;
            setMonto(''); setDescripcion(''); setShowModal(false);
            await cargarMovimientos();
        } catch(e) {
            if (requestId !== registrarRequestRef.current || requestSessionId !== currentSessionRef.current) return;
            console.error(e); alert('Error al registrar movimiento');
        }
        finally {
            if (requestId === registrarRequestRef.current && requestSessionId === currentSessionRef.current) setLoading(false);
        }
    };

    const { totalEntradas, totalSalidas, saldoActual } = useMemo(() => {
        const mov = movimientos.filter(m => m.sesion === sesionId);
        const ent = mov.filter(m=>m.tipo==='entrada').reduce((s,m)=>s+Number(m.monto),0);
        const sal = mov.filter(m=>m.tipo==='salida').reduce((s,m)=>s+Number(m.monto),0);
        return { totalEntradas:ent, totalSalidas:sal, saldoActual:Number(saldoInicial)+ent-sal };
    }, [movimientos, saldoInicial, sesionId]);

    return (
        <>
            <style>{css}</style>
            <div className="gmc gmc-fadeup">

                {/* Cabecera + botón */}
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'16px'}}>
                    <div>
                        <p style={{fontSize:'.72rem',fontWeight:'800',color:C.brand,textTransform:'uppercase',letterSpacing:'.1em',marginBottom:'2px'}}>Caja POS</p>
                        <h3 style={{fontSize:'1.05rem',fontWeight:'900',color:'#0f172a',letterSpacing:'-.02em'}}>Movimientos de Caja</h3>
                        <div style={{display:'flex',gap:'4px',marginTop:'6px'}}>
                            {[C.brand, C.accent1, C.accent2, C.accent3].map((c,i)=><div key={i} style={{height:'3px',width:i===0?'20px':'7px',borderRadius:'2px',background:c}}/>)}
                        </div>
                    </div>
                    <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:'6px'}}>
                        <button className="gmc-btn-primary" onClick={()=>setShowModal(true)} disabled={!operadorToken}
                            title={!operadorToken?'Identifica al operador de caja con su PIN para registrar movimientos':undefined}
                            style={{background:`linear-gradient(135deg,${C.brand},${C.accent1})`,color:'#fff',boxShadow:`0 4px 14px rgba(97,37,123,.28)`,opacity:!operadorToken?.5:1,cursor:!operadorToken?'not-allowed':'pointer'}}
                            onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.transform='translateY(-1px)'}
                            onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.transform='translateY(0)'}>
                            <Plus size={15}/> Nuevo Movimiento
                        </button>
                        {!operadorToken&&(
                            <p role="note" style={{fontSize:'.68rem',color:'#92400e',fontWeight:'600',textAlign:'right'}}>Identifica al operador de caja (PIN) para registrar movimientos.</p>
                        )}
                    </div>
                </div>

                {/* Stats cards */}
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:'12px',marginBottom:'18px'}}>
                    {[
                        {label:'Saldo Inicial',value:Number(saldoInicial),color:'#64748b',bg:'#f8fafc',border:'#e2e8f0',icon:<Wallet size={16} color="#64748b"/>},
                        {label:'Entradas',value:totalEntradas,color:'#16a34a',bg:'#f0fdf4',border:'#bbf7d0',icon:<TrendingUp size={16} color="#16a34a"/>},
                        {label:'Salidas',value:totalSalidas,color:C.accent1,bg:`${C.accent1}0c`,border:`${C.accent1}25`,icon:<TrendingDown size={16} color={C.accent1}/>},
                        {label:'Saldo Actual',value:saldoActual,color:C.brand,bg:`${C.brand}0c`,border:`${C.brand}25`,icon:<DollarSign size={16} color={C.brand}/>},
                    ].map(s=>(
                        <div key={s.label} style={{background:s.bg,border:`1.5px solid ${s.border}`,borderRadius:'14px',padding:'12px 14px'}}>
                            <div style={{display:'flex',alignItems:'center',gap:'6px',marginBottom:'6px'}}>
                                {s.icon}
                                <span style={{fontSize:'.68rem',fontWeight:'800',color:s.color,textTransform:'uppercase',letterSpacing:'.06em'}}>{s.label}</span>
                            </div>
                            <p style={{fontSize:'1.1rem',fontWeight:'900',color:'#0f172a'}}>{s.value.toFixed(2)} <span style={{fontSize:'.72rem',fontWeight:'700',color:'#94a3b8'}}>Bs.</span></p>
                        </div>
                    ))}
                </div>

                {/* Tabla */}
                {cargando ? (
                    <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'10px',padding:'32px'}}>
                        <div style={{width:'28px',height:'28px',border:`3px solid ${C.brand}`,borderTopColor:'transparent',borderRadius:'50%',animation:'gmcSpin .7s linear infinite'}}/>
                        <p style={{color:'#64748b',fontWeight:'600',fontSize:'.85rem'}}>Cargando movimientos...</p>
                    </div>
                ) : (
                    <div className="gmc-scroll" style={{overflowX:'auto',borderRadius:'14px',border:'1.5px solid #f1f5f9'}}>
                        <table style={{width:'100%',borderCollapse:'collapse',fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                            <thead>
                                <tr style={{background:'#fafcff',borderBottom:'1.5px solid #f1f5f9'}}>
                                    {['Fecha','Tipo','Monto','Descripción','Usuario'].map(h=>(
                                        <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:'.65rem',fontWeight:'800',color:'#94a3b8',textTransform:'uppercase',letterSpacing:'.08em',whiteSpace:'nowrap'}}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {movimientos.map(m=>(
                                    <tr key={m.id} className="gmc-row" style={{borderBottom:'1px solid #f8fafc'}}>
                                        <td style={{padding:'10px 14px',whiteSpace:'nowrap',fontSize:'.78rem',color:'#64748b',fontWeight:'500'}}>
                                            {new Date(m.fecha).toLocaleString('es-ES',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'})}
                                        </td>
                                        <td style={{padding:'10px 14px',whiteSpace:'nowrap'}}>
                                            <span style={{display:'inline-flex',alignItems:'center',gap:'4px',padding:'3px 9px',borderRadius:'7px',fontSize:'.72rem',fontWeight:'800',background:m.tipo==='entrada'?'#f0fdf4':`${C.accent1}0c`,border:`1px solid ${m.tipo==='entrada'?'#bbf7d0':`${C.accent1}25`}`,color:m.tipo==='entrada'?'#16a34a':C.accent1Dark}}>
                                                {m.tipo==='entrada'?<ArrowUpCircle size={11}/>:<ArrowDownCircle size={11}/>}
                                                {m.tipo==='entrada'?'Entrada':'Salida'}
                                            </span>
                                        </td>
                                        <td style={{padding:'10px 14px',whiteSpace:'nowrap'}}>
                                            <span style={{fontSize:'.9rem',fontWeight:'800',color:m.tipo==='entrada'?'#16a34a':C.accent1Dark}}>{Number(m.monto).toFixed(2)} <span style={{fontSize:'.72rem',fontWeight:'600',color:'#94a3b8'}}>Bs.</span></span>
                                        </td>
                                        <td style={{padding:'10px 14px',fontSize:'.82rem',color:'#374151',maxWidth:'200px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{m.descripcion||'—'}</td>
                                        <td style={{padding:'10px 14px',whiteSpace:'nowrap',fontSize:'.78rem',color:'#64748b',fontWeight:'600'}}>{m.usuario_nombre}</td>
                                    </tr>
                                ))}
                                {movimientos.length===0&&(
                                    <tr>
                                        <td colSpan={5} style={{padding:'40px',textAlign:'center'}}>
                                            <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:'10px'}}>
                                                <div style={{width:'46px',height:'46px',borderRadius:'14px',background:`${C.brand}10`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                                                    <FileText size={22} color={C.brand} style={{opacity:.6}}/>
                                                </div>
                                                <p style={{fontSize:'.88rem',fontWeight:'800',color:'#0f172a'}}>Sin movimientos</p>
                                                <p style={{fontSize:'.75rem',color:'#94a3b8'}}>Registra el primer movimiento de esta sesión</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal superior de nuevo movimiento, contenido dentro del diálogo de caja */}
            {showModal&&(
                <>
                    <style>{css}</style>
                    <div className="gmc" style={{position:'fixed',inset:0,zIndex:99999,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px',background:'rgba(15,23,42,.55)',backdropFilter:'blur(6px)',animation:'gmcFadeIn .2s ease'}}>
                        <div ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="gmc-modal-title" tabIndex={-1} onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:'22px',width:'100%',maxWidth:'440px',maxHeight:'calc(100dvh - 32px)',overflowY:'auto',boxShadow:'0 32px 80px rgba(0,0,0,.2)',border:'1.5px solid #f1f5f9',animation:'gmcFadeUp .3s cubic-bezier(.16,1,.3,1)'}}>
                            <div style={{height:'4px',background:`linear-gradient(90deg,${C.brand},${C.accent1},${C.accent2},${C.accent3})`}}/>

                            {/* Header modal */}
                            <div style={{padding:'20px 22px 0',display:'flex',alignItems:'flex-start',justifyContent:'space-between'}}>
                                <div>
                                    <div style={{display:'flex',alignItems:'center',gap:'9px',marginBottom:'6px'}}>
                                        <div style={{width:'34px',height:'34px',borderRadius:'10px',background:`linear-gradient(135deg,${C.brand},${C.accent1})`,display:'flex',alignItems:'center',justifyContent:'center',boxShadow:`0 3px 10px rgba(97,37,123,.25)`}}>
                                            <Plus size={16} color="#fff"/>
                                        </div>
                                        <div>
                                            <h3 id="gmc-modal-title" style={{fontSize:'1rem',fontWeight:'900',color:'#0f172a',letterSpacing:'-.02em'}}>Registrar Movimiento</h3>
                                            <p style={{fontSize:'.72rem',color:'#94a3b8'}}>Ingresa los datos del movimiento de caja</p>
                                        </div>
                                    </div>
                                    <div style={{display:'flex',gap:'4px',marginBottom:'4px'}}>
                                        {[C.brand, C.accent1, C.accent2, C.accent3].map((c,i)=><div key={i} style={{height:'2px',width:i===0?'16px':'5px',borderRadius:'2px',background:c}}/>)}
                                    </div>
                                </div>
                                <button aria-label="Cerrar registro de movimiento" onClick={()=>{setShowModal(false);setMonto('');setDescripcion('');}} style={{width:'30px',height:'30px',borderRadius:'9px',border:'1.5px solid #f1f5f9',background:'#f8fafc',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',flexShrink:0}}
                                    onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background='#f1f5f9'}
                                    onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background='#f8fafc'}>
                                    <X size={14} color="#64748b"/>
                                </button>
                            </div>

                            {/* Body modal */}
                            <div style={{padding:'16px 22px 22px',display:'flex',flexDirection:'column',gap:'14px'}}>
                                {/* Tipo */}
                                <div>
                                    <span id="gmc-tipo-label" style={{display:'block',fontSize:'.68rem',fontWeight:'800',color:'#374151',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:'8px'}}>Tipo de movimiento *</span>
                                    <div role="group" aria-labelledby="gmc-tipo-label" style={{display:'flex',gap:'8px'}}>
                                        <button type="button" aria-pressed={tipo==='entrada'} className="gmc-type-btn" onClick={()=>setTipo('entrada')}
                                            style={{borderColor:tipo==='entrada'?'#16a34a':'#e2e8f0',background:tipo==='entrada'?'#f0fdf4':'#f8fafc',color:tipo==='entrada'?'#16a34a':'#64748b'}}>
                                            <ArrowUpCircle size={15}/> Entrada
                                        </button>
                                        <button type="button" aria-pressed={tipo==='salida'} className="gmc-type-btn" onClick={()=>setTipo('salida')}
                                            style={{borderColor:tipo==='salida'?C.accent1:'#e2e8f0',background:tipo==='salida'?`${C.accent1}0c`:'#f8fafc',color:tipo==='salida'?C.accent1Dark:'#64748b'}}>
                                            <ArrowDownCircle size={15}/> Salida
                                        </button>
                                    </div>
                                </div>

                                {/* Monto */}
                                <div>
                                    <label htmlFor="gmc-monto" style={{display:'block',fontSize:'.68rem',fontWeight:'800',color:'#374151',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:'7px'}}>Monto (Bs.) *</label>
                                    <div style={{position:'relative'}}>
                                        <input id="gmc-monto" type="number" step="0.01" min="0.01" value={monto} onChange={e=>setMonto(e.target.value)} placeholder="0.00" className="gmc-input" style={{paddingRight:'36px'}}/>
                                        <span style={{position:'absolute',right:'12px',top:'50%',transform:'translateY(-50%)',fontSize:'.72rem',color:'#94a3b8',fontWeight:'700',pointerEvents:'none'}}>Bs.</span>
                                    </div>
                                </div>

                                {/* Descripción */}
                                <div>
                                    <label htmlFor="gmc-descripcion" style={{display:'block',fontSize:'.68rem',fontWeight:'800',color:'#374151',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:'7px'}}>Descripción</label>
                                    <textarea id="gmc-descripcion" value={descripcion} onChange={e=>setDescripcion(e.target.value)} placeholder="Descripción del movimiento..." rows={3} className="gmc-input" style={{resize:'none',minHeight:'72px'}}/>
                                </div>

                                {/* Botones */}
                                <div style={{display:'flex',gap:'10px',paddingTop:'2px'}}>
                                    <button className="gmc-btn-ghost" onClick={()=>{setShowModal(false);setMonto('');setDescripcion('');}} disabled={loading}>Cancelar</button>
                                    <button className="gmc-btn-primary" onClick={handleRegistrar} disabled={!monto||loading}
                                        style={{flex:1,background:tipo==='entrada'?'linear-gradient(135deg,#16a34a,#15803d)':`linear-gradient(135deg,${C.brand},${C.accent1})`,color:'#fff',justifyContent:'center',boxShadow:tipo==='entrada'?'0 4px 14px rgba(22,163,74,.3)':`0 4px 14px rgba(97,37,123,.28)`,opacity:!monto||loading?.5:1}}>
                                        {loading
                                            ?<><span style={{width:'12px',height:'12px',border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'gmcSpin .7s linear infinite',display:'inline-block'}}/> Registrando...</>
                                            :<>{tipo==='entrada'?<ArrowUpCircle size={14}/>:<ArrowDownCircle size={14}/>} Registrar {tipo==='entrada'?'Entrada':'Salida'}</>
                                        }
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default GestionMovimientosCaja;
